import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  SegmentListMultipleItemMembershipOperator,
  SegmentListSingleItemMembershipOperator,
} from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import { ProductOrderedSegment } from "@redotech/redo-conversion-core-common/post-purchase/post-purchase-flow";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import {
  ModalProduct,
  ProductSelectionModal,
} from "@redotech/redo-web/product/product-selection-modal";
import { Text } from "@redotech/redo-web/text";
import { ProductInfo } from "@redotech/shopify-storefront/products";
import {
  convertShopifyIdToGid,
  Gid,
  parseNumericIdFromShopifyId,
  ShopifyIdType,
} from "@redotech/shopify-util/id";
import { memo, useState } from "react";
import { OperatorSelector } from "../../operator-selector";
import * as css from "./products-ordered.module.css";

interface ProductsOrderedSegmentCommonProps {
  condition: ProductOrderedSegment;
  getProductsByGids: (ids: Gid[]) => Promise<ProductInfo[]>;
}

export type ProductsOrderedSegmentProps = ProductsOrderedSegmentCommonProps &
  (
    | {
        readonly: false;
        setCondition: (condition: ProductOrderedSegment) => void;
        searchProducts: (params: { search: string }) => Promise<ModalProduct[]>;
      }
    | { readonly: true }
  );

const segmentListMultipleItemMembershipOperatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "At least one",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "No",
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "Every",
};

const segmentListSingleItemMembershipOperatorToText: Record<
  SegmentListSingleItemMembershipOperator,
  string
> = {
  [SegmentListSingleItemMembershipOperator.IN]: "is",
  [SegmentListSingleItemMembershipOperator.NOT_IN]: "is not",
};

export const ProductsOrderedSegmentCondition = memo(
  function ProductsOrderedSegmentCondition({
    condition,
    getProductsByGids,
    ...props
  }: ProductsOrderedSegmentProps) {
    const [modalOpen, setModalOpen] = useState(false);

    const productNamesLoad = useLoad(async () => {
      if (condition.productIds.length === 0) {
        return { productNames: {}, variantNames: {} };
      }

      const gids = condition.productIds.map((id) =>
        convertShopifyIdToGid(id, ShopifyIdType.Product),
      );
      const products = await getProductsByGids(gids);
      const productNames: { [gid: string]: string } = {};
      const variantNames: { [variantGid: string]: string } = {};
      for (const product of products) {
        productNames[product.id] = product.title;
        for (const variant of product.variants.nodes) {
          variantNames[variant.id] = `${product.title} – ${variant.title}`;
        }
      }

      return { productNames, variantNames };
    }, [condition.productIds, getProductsByGids]);

    const handleCartOperatorSelected = useHandler(
      (cartOperator: SegmentListMultipleItemMembershipOperator) => {
        if (!props.readonly) {
          props.setCondition({ ...condition, cartOperator });
        }
      },
    );

    const handleProductListOperatorSelected = useHandler(
      (productListOperator: SegmentListSingleItemMembershipOperator) => {
        if (!props.readonly) {
          props.setCondition({ ...condition, productListOperator });
        }
      },
    );

    const handleAddProduct = useHandler(() => {
      if (!props.readonly) {
        setModalOpen(true);
      }
    });

    const handleRemoveProduct = useHandler(
      (productId: string, variantId?: string) => {
        if (!props.readonly) {
          if (variantId) {
            const updatedVariantFilterItems = condition.variantFilterItems
              ?.map((i) =>
                i.productId === productId
                  ? {
                      ...i,
                      variantIds: i.variantIds.filter((v) => v !== variantId),
                    }
                  : i,
              )
              .filter((i) => i.variantIds.length > 0);
            const productStillHasVariants = updatedVariantFilterItems?.some(
              (i) => i.productId === productId,
            );
            props.setCondition({
              ...condition,
              productIds: productStillHasVariants
                ? condition.productIds
                : condition.productIds.filter((id) => id !== productId),
              variantFilterItems:
                updatedVariantFilterItems &&
                updatedVariantFilterItems.length > 0
                  ? updatedVariantFilterItems
                  : undefined,
            });
          } else {
            props.setCondition({
              ...condition,
              productIds: condition.productIds.filter((id) => id !== productId),
              variantFilterItems: condition.variantFilterItems?.filter(
                (i) => i.productId !== productId,
              ),
            });
          }
        }
      },
    );

    if (condition.productIds.length > 0 && !productNamesLoad.value) {
      return (
        <Flex align="center" dir="row" justify="center" p="md" w="full">
          <LoadingRedoAnimation className={css.smallSpinner} />
        </Flex>
      );
    }

    return (
      <>
        <OperatorSelector<SegmentListMultipleItemMembershipOperator>
          availableOptions={Object.values(
            SegmentListMultipleItemMembershipOperator,
          )}
          onOperatorSelected={handleCartOperatorSelected}
          operatorToText={segmentListMultipleItemMembershipOperatorToText}
          readonly={props.readonly}
          selectedOperator={condition.cartOperator}
        />
        <Text fontSize="xs" textColor="primary">
          product in the cart
        </Text>
        <OperatorSelector<SegmentListSingleItemMembershipOperator>
          availableOptions={Object.values(
            SegmentListSingleItemMembershipOperator,
          )}
          onOperatorSelected={handleProductListOperatorSelected}
          operatorToText={segmentListSingleItemMembershipOperatorToText}
          readonly={props.readonly}
          selectedOperator={condition.productListOperator}
        />
        <Text fontSize="xs" textColor="primary">
          in this list:
        </Text>
        <Flex dir="row" gap="md" w="full" wrap="wrap">
          {condition.productIds.length === 0 ? (
            <Text fontSize="xs" textColor="secondary">
              No products selected
            </Text>
          ) : (
            condition.productIds.flatMap((productId) => {
              const variantEntry = condition.variantFilterItems?.find(
                (i) => i.productId === productId,
              );

              if (variantEntry) {
                return variantEntry.variantIds.map((variantGid) => {
                  const variantName =
                    productNamesLoad.value?.variantNames?.[variantGid] ??
                    variantGid;
                  return (
                    <RedoBadge
                      color="gray"
                      iconTrailing={
                        props.readonly
                          ? undefined
                          : {
                              type: "icon",
                              onClick: () =>
                                handleRemoveProduct(productId, variantGid),
                              Icon: TrashIcon,
                            }
                      }
                      key={variantGid}
                      size="sm"
                      text={variantName}
                    />
                  );
                });
              }

              const productName =
                productNamesLoad.value?.productNames?.[productId] ||
                parseNumericIdFromShopifyId(productId);
              return (
                <RedoBadge
                  color="gray"
                  iconTrailing={
                    props.readonly
                      ? undefined
                      : {
                          type: "icon",
                          onClick: () => handleRemoveProduct(productId),
                          Icon: TrashIcon,
                        }
                  }
                  key={productId}
                  size="sm"
                  text={productName}
                />
              );
            })
          )}
        </Flex>
        {!props.readonly && (
          <RedoButton
            hierarchy="secondary"
            onClick={handleAddProduct}
            size="xs"
            text="Add product"
          />
        )}
        {!props.readonly && (
          <ProductSelectionModal
            multiselect
            open={modalOpen}
            searchProducts={props.searchProducts}
            setOpen={setModalOpen}
            setSelected={(selected) => {
              if (selected) {
                const newProductIds: string[] = [];
                const newVariantFilterItems: {
                  productId: string;
                  variantIds: string[];
                }[] = [];
                for (const item of selected) {
                  newProductIds.push(item.product.id);
                  if (item.variants.length < item.product.variants.length) {
                    newVariantFilterItems.push({
                      productId: item.product.id,
                      variantIds: item.variants.map(
                        (v) => v.admin_graphql_api_id,
                      ),
                    });
                  }
                }
                const existingProductIds = condition.productIds.filter(
                  (id) => !newProductIds.includes(id),
                );
                const existingVariantFilterItems = (
                  condition.variantFilterItems ?? []
                ).filter((i) => !newProductIds.includes(i.productId));
                const mergedVariantFilterItems = [
                  ...existingVariantFilterItems,
                  ...newVariantFilterItems,
                ];
                props.setCondition({
                  ...condition,
                  productIds: [...existingProductIds, ...newProductIds],
                  variantFilterItems:
                    mergedVariantFilterItems.length > 0
                      ? mergedVariantFilterItems
                      : undefined,
                });
              }
            }}
          />
        )}
      </>
    );
  },
);
